import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ChartBar, Target, MapPin, TrendUp, TrendDown, CaretLeft, CaretRight,
  DownloadSimple, Pencil, CheckCircle, Users, Folder, CurrencyDollar,
  Buildings, ArrowsClockwise
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line
} from 'recharts';
import {
  getKpiDashboard, getKpiQuarterlyReport, setKpiBulkObjectives, downloadKpiReportPdf
} from '../services/api';

const TERRITORY_COLORS = {
  Guadeloupe: '#2D5A4C',
  Martinique: '#D47A62',
  Guyane: '#FF8E00',
  Reunion: '#3B82F6',
  Mayotte: '#8B5CF6',
};

const KPI_ICONS = {
  nouveaux_contacts: Users,
  dossiers_crees: Folder,
  dossiers_valides: CheckCircle,
  montants_collectes: CurrencyDollar,
  organisations_accompagnees: Buildings,
  taux_conversion: TrendUp,
};

function ProgressBar({ percent, color }) {
  const capped = Math.min(percent, 100);
  const isOverTarget = percent > 100;
  return (
    <div className="relative w-full h-2 bg-[#F0EFEB] rounded-full overflow-hidden">
      <div
        className="absolute left-0 top-0 h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${capped}%`, background: isOverTarget ? '#10B981' : color || '#2D5A4C' }}
      />
    </div>
  );
}

function TerritoryCard({ data, onEdit }) {
  const { territory, kpis, progress } = data;
  const color = TERRITORY_COLORS[territory] || '#2D5A4C';
  const avgProgress = useMemo(() => {
    const vals = Object.values(progress).filter(p => p.target > 0).map(p => p.percent);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  }, [progress]);

  return (
    <Card className="border-[#E8E6E1] hover:shadow-md transition-shadow" data-testid={`territory-card-${territory}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: color }} />
            <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>{territory}</CardTitle>
          </div>
          <div className="flex items-center gap-1.5">
            {avgProgress > 0 && (
              <Badge className={`text-[9px] ${avgProgress >= 100 ? 'bg-emerald-100 text-emerald-700' : avgProgress >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-[#F0EFEB] text-[#5E6D66]'}`}>
                {avgProgress}% moy.
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {Object.entries(progress).map(([key, p]) => {
          const Icon = KPI_ICONS[key] || ChartBar;
          const def = data.kpis ? null : null;
          const isPercent = key === 'taux_conversion';
          return (
            <div key={key} className="space-y-1" data-testid={`kpi-${territory}-${key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Icon size={12} className="text-[#5E6D66]" />
                  <span className="text-[10px] text-[#5E6D66] capitalize">{key.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-[#1A2421]">
                    {isPercent ? `${p.actual}%` : p.actual.toLocaleString('fr-FR')}
                  </span>
                  {p.target > 0 && (
                    <span className="text-[10px] text-[#5E6D66]">
                      / {isPercent ? `${p.target}%` : p.target.toLocaleString('fr-FR')}
                    </span>
                  )}
                </div>
              </div>
              {p.target > 0 && <ProgressBar percent={p.percent} color={color} />}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function KpiPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editTargets, setEditTargets] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, repRes] = await Promise.all([
        getKpiDashboard(year, quarter, useCustomRange ? customStart : null, useCustomRange ? customEnd : null),
        getKpiQuarterlyReport(year, quarter),
      ]);
      setDashboard(dashRes.data);
      setReport(repRes.data);
    } catch (e) {
      toast.error('Erreur chargement KPI');
    }
    setLoading(false);
  }, [year, quarter, useCustomRange, customStart, customEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateQuarter = (dir) => {
    let newQ = quarter + dir;
    let newY = year;
    if (newQ > 4) { newQ = 1; newY++; }
    if (newQ < 1) { newQ = 4; newY--; }
    setQuarter(newQ);
    setYear(newY);
  };

  const openEdit = () => {
    const init = {};
    (dashboard?.territories || []).forEach(t => {
      init[t.territory] = { ...t.objectives };
    });
    setEditTargets(init);
    setEditOpen(true);
  };

  const handleSaveObjectives = async () => {
    setSaving(true);
    try {
      await setKpiBulkObjectives({ year, quarter, territories: editTargets });
      toast.success(`Objectifs T${quarter} ${year} sauvegardes`);
      setEditOpen(false);
      fetchData();
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await downloadKpiReportPdf(year, quarter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_kpi_T${quarter}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exporte');
    } catch {
      toast.error('Erreur export PDF');
    }
    setExporting(false);
  };

  const updateTarget = (territory, kpiKey, value) => {
    setEditTargets(prev => ({
      ...prev,
      [territory]: { ...(prev[territory] || {}), [kpiKey]: parseFloat(value) || 0 },
    }));
  };

  // Chart data
  const barChartData = useMemo(() => {
    if (!dashboard?.territories) return [];
    return dashboard.kpi_definitions.map(kd => {
      const row = { name: kd.label.split(' ').slice(0, 2).join(' ') };
      dashboard.territories.forEach(t => {
        row[t.territory] = t.kpis[kd.key] || 0;
      });
      return row;
    });
  }, [dashboard]);

  const historyChartData = useMemo(() => {
    if (!report?.history) return [];
    return report.history.map(h => ({
      name: h.label,
      Contacts: h.nouveaux_contacts,
      Dossiers: h.dossiers_crees,
      Montants: h.montants_collectes,
    }));
  }, [report]);

  const radarData = useMemo(() => {
    if (!dashboard?.territories) return [];
    return dashboard.kpi_definitions.filter(kd => kd.key !== 'montants_collectes').map(kd => {
      const row = { kpi: kd.label.split(' ').slice(0, 2).join(' ') };
      dashboard.territories.forEach(t => {
        const prog = t.progress[kd.key];
        row[t.territory] = prog?.percent || 0;
      });
      return row;
    });
  }, [dashboard]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kpi-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Suivi KPI"
        subtitle="Indicateurs de performance trimestriels vs objectifs"
        icon={<Target size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#FF8E00', '#80C51F']}
      />

      {/* Global summary */}
      {dashboard?.global && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="global-kpis">
          {dashboard.kpi_definitions.map(kd => {
            const Icon = KPI_ICONS[kd.key] || ChartBar;
            const val = dashboard.global[kd.key];
            return (
              <Card key={kd.key} className="border-[#E8E6E1]" data-testid={`global-kpi-${kd.key}`}>
                <CardContent className="p-3 flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#2D5A4C]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider truncate">{kd.label}</p>
                    <p className="text-base font-bold text-[#1A2421]">
                      {kd.unit === 'EUR' ? `${val.toLocaleString('fr-FR')} EUR` : kd.unit === '%' ? `${val}%` : val.toLocaleString('fr-FR')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F0EFEB]">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Territoires</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts">Graphiques</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">Rapport</TabsTrigger>
        </TabsList>

        {/* Tab: Territory cards */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4" data-testid="territory-cards-grid">
            {(dashboard?.territories || []).map(t => (
              <TerritoryCard key={t.territory} data={t} onEdit={openEdit} />
            ))}
          </div>
        </TabsContent>

        {/* Tab: Charts */}
        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            {/* Bar Chart */}
            <Card className="border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Comparaison inter-territoires</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EFEB" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E8E6E1' }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {Object.entries(TERRITORY_COLORS).map(([terr, color]) => (
                      <Bar key={terr} dataKey={terr} fill={color} radius={[2, 2, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card className="border-[#E8E6E1]">
              <CardHeader>
                <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Progression vers objectifs (%)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E8E6E1" />
                    <PolarAngleAxis dataKey="kpi" tick={{ fontSize: 9 }} />
                    <PolarRadiusAxis tick={{ fontSize: 8 }} domain={[0, 100]} />
                    {Object.entries(TERRITORY_COLORS).map(([terr, color]) => (
                      <Radar key={terr} name={terr} dataKey={terr} stroke={color} fill={color} fillOpacity={0.15} />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* History Chart */}
            <Card className="border-[#E8E6E1] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Evolution trimestrielle {year} (tous territoires)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={historyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EFEB" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E8E6E1' }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="Contacts" stroke="#2D5A4C" strokeWidth={2} dot={{ fill: '#2D5A4C', r: 4 }} />
                    <Line type="monotone" dataKey="Dossiers" stroke="#D47A62" strokeWidth={2} dot={{ fill: '#D47A62', r: 4 }} />
                    <Line type="monotone" dataKey="Montants" stroke="#FF8E00" strokeWidth={2} dot={{ fill: '#FF8E00', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Quarterly Report */}
        <TabsContent value="report">
          <Card className="border-[#E8E6E1] mt-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>
                  Rapport trimestriel T{quarter} {year}
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting} className="text-xs" data-testid="report-export-pdf">
                  {exporting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <DownloadSimple size={14} className="mr-1" />}
                  Exporter PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid="quarterly-report-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-[#2D5A4C]">
                      <th className="text-left py-2 px-2 text-[#2D5A4C] font-bold">Territoire</th>
                      {dashboard?.kpi_definitions?.map(kd => (
                        <th key={kd.key} className="text-center py-2 px-2 text-[#2D5A4C] font-bold whitespace-nowrap">{kd.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.territories || []).map(row => (
                      <tr key={row.territory} className="border-b border-[#F0EFEB] hover:bg-[#F9F8F5]" data-testid={`report-row-${row.territory}`}>
                        <td className="py-2.5 px-2 font-semibold text-[#1A2421] flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: TERRITORY_COLORS[row.territory] }} />
                          {row.territory}
                        </td>
                        {dashboard?.kpi_definitions?.map(kd => {
                          const cell = row[kd.key] || {};
                          const isPercent = kd.unit === '%';
                          return (
                            <td key={kd.key} className="text-center py-2.5 px-2">
                              <span className="font-bold text-[#1A2421]">
                                {isPercent ? `${cell.actual || 0}%` : (cell.actual || 0).toLocaleString('fr-FR')}
                                {kd.unit === 'EUR' ? ' EUR' : ''}
                              </span>
                              {cell.target > 0 && (
                                <>
                                  <span className="text-[#5E6D66]"> / {isPercent ? `${cell.target}%` : cell.target.toLocaleString('fr-FR')}{kd.unit === 'EUR' ? ' EUR' : ''}</span>
                                  <div className="mt-1">
                                    <Badge className={`text-[8px] ${cell.percent >= 100 ? 'bg-emerald-100 text-emerald-700' : cell.percent >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                                      {cell.percent}%
                                    </Badge>
                                  </div>
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Global row */}
                    {dashboard?.global && (
                      <tr className="border-t-2 border-[#2D5A4C] bg-[#F9F8F5] font-bold">
                        <td className="py-2.5 px-2 text-[#2D5A4C]">TOTAL</td>
                        {dashboard.kpi_definitions.map(kd => (
                          <td key={kd.key} className="text-center py-2.5 px-2 text-[#2D5A4C]">
                            {kd.unit === '%' ? `${dashboard.global[kd.key]}%` :
                             kd.unit === 'EUR' ? `${dashboard.global[kd.key].toLocaleString('fr-FR')} EUR` :
                             dashboard.global[kd.key].toLocaleString('fr-FR')}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Objectives Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="edit-objectives-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Objectifs T{quarter} {year}</DialogTitle>
            <DialogDescription className="text-xs">Definissez les objectifs trimestriels pour chaque territoire et indicateur.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E8E6E1]">
                  <th className="text-left py-2 px-1 text-[10px] text-[#5E6D66] uppercase">Territoire</th>
                  {dashboard?.kpi_definitions?.map(kd => (
                    <th key={kd.key} className="text-center py-2 px-1 text-[10px] text-[#5E6D66] uppercase whitespace-nowrap">{kd.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(TERRITORY_COLORS).map(terr => (
                  <tr key={terr} className="border-b border-[#F0EFEB]">
                    <td className="py-2 px-1 font-semibold text-[#1A2421] flex items-center gap-1.5 whitespace-nowrap">
                      <div className="w-2 h-2 rounded-full" style={{ background: TERRITORY_COLORS[terr] }} />
                      {terr}
                    </td>
                    {dashboard?.kpi_definitions?.map(kd => (
                      <td key={kd.key} className="py-2 px-1">
                        <Input
                          type="number"
                          value={editTargets[terr]?.[kd.key] || ''}
                          onChange={e => updateTarget(terr, kd.key, e.target.value)}
                          className="h-7 text-[11px] w-20 text-center mx-auto"
                          placeholder="0"
                          data-testid={`target-${terr}-${kd.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E8E6E1]">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button size="sm" onClick={handleSaveObjectives} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-objectives-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Target size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
